import React from 'react';
import { SupplierData } from '../data/mockData';
import { XIcon, MapPinIcon, PhoneIcon, MailIcon, AlertTriangleIcon, CheckCircleIcon, ClockIcon, TruckIcon, PackageIcon, BarChart3Icon } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
interface SupplierDetailPanelProps {
  supplier: SupplierData;
  onClose: () => void;
}
// Generate some mock performance data for the charts
const generatePerformanceData = (supplier: SupplierData) => {
  const baseOnTime = supplier.metrics?.onTimeDelivery || 90;
  const baseQuality = supplier.metrics?.qualityRating || 85;
  return [{
    month: 'Jan',
    onTimeDelivery: baseOnTime - 5,
    qualityRating: baseQuality - 3
  }, {
    month: 'Feb',
    onTimeDelivery: baseOnTime - 2,
    qualityRating: baseQuality - 1
  }, {
    month: 'Mar',
    onTimeDelivery: baseOnTime,
    qualityRating: baseQuality
  }, {
    month: 'Apr',
    onTimeDelivery: baseOnTime + 1,
    qualityRating: baseQuality + 2
  }, {
    month: 'May',
    onTimeDelivery: baseOnTime - 1,
    qualityRating: baseQuality + 1
  }, {
    month: 'Jun',
    onTimeDelivery: baseOnTime + 2,
    qualityRating: baseQuality - 2
  }];
};
// Generate some mock volume data
const generateVolumeData = (supplier: SupplierData) => {
  const baseVolume = supplier.tier === 1 ? 400 : supplier.tier === 2 ? 250 : 150;
  return [{
    category: 'Product A',
    volume: baseVolume + Math.floor(Math.random() * 50)
  }, {
    category: 'Product B',
    volume: baseVolume - Math.floor(Math.random() * 50)
  }, {
    category: 'Product C',
    volume: baseVolume / 2 + Math.floor(Math.random() * 30)
  }, {
    category: 'Product D',
    volume: baseVolume / 3 + Math.floor(Math.random() * 20)
  }];
};
const SupplierDetailPanel: React.FC<SupplierDetailPanelProps> = ({
  supplier,
  onClose
}) => {
  // Get status color
  const getStatusColor = (status: string) => {
    return status === 'At Risk' ? 'text-[#e53935] bg-red-50' : status === 'Delayed' ? 'text-[#fbc02d] bg-yellow-50' : 'text-[#4caf50] bg-green-50';
  };
  // Get status icon
  const getStatusIcon = (status: string) => {
    return status === 'At Risk' ? <AlertTriangleIcon size={16} data-id="element-174" /> : status === 'Delayed' ? <ClockIcon size={16} data-id="element-175" /> : <CheckCircleIcon size={16} data-id="element-176" />;
  };
  // Generate mock data for charts
  const performanceData = generatePerformanceData(supplier);
  const volumeData = generateVolumeData(supplier);
  return <div className="h-full flex flex-col" data-id="element-177">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-178">
        <h2 className="text-lg font-bold text-[#333333]" data-id="element-179">Supplier Details</h2>
        <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100" aria-label="Close details" data-id="element-180">
          <XIcon size={20} className="text-gray-500" data-id="element-181" />
        </button>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4" data-id="element-182">
        {/* Supplier header */}
        <div className="mb-6" data-id="element-183">
          <h3 className="text-xl font-bold text-[#005a9e]" data-id="element-184">{supplier.name}</h3>
          <div className="flex items-center mt-2" data-id="element-185">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(supplier.status)}`} data-id="element-186">
              {getStatusIcon(supplier.status)}
              <span className="ml-1" data-id="element-187">{supplier.status}</span>
            </span>
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-188">
              Tier {supplier.tier}
            </span>
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-189">
              {supplier.category}
            </span>
          </div>
        </div>
        {/* Contact information */}
        <div className="bg-gray-50 p-3 rounded-lg mb-6" data-id="element-190">
          <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-191">
            Contact Information
          </h4>
          <div className="flex items-center mb-2" data-id="element-192">
            <MapPinIcon size={16} className="text-gray-400 mr-2" data-id="element-193" />
            <span className="text-sm" data-id="element-194">
              {supplier.address}, {supplier.region}
            </span>
          </div>
          <div className="flex items-center mb-2" data-id="element-195">
            <PhoneIcon size={16} className="text-gray-400 mr-2" data-id="element-196" />
            <span className="text-sm" data-id="element-197">+1 (555) 123-4567</span>
          </div>
          <div className="flex items-center" data-id="element-198">
            <MailIcon size={16} className="text-gray-400 mr-2" data-id="element-199" />
            <span className="text-sm" data-id="element-200">
              contact@{supplier.name.toLowerCase().replace(/\s+/g, '')}.com
            </span>
          </div>
        </div>
        {/* Performance metrics */}
        <div className="mb-6" data-id="element-201">
          <h4 className="text-sm font-medium text-gray-500 mb-3" data-id="element-202">
            Performance Metrics
          </h4>
          <div className="space-y-3" data-id="element-203">
            <div data-id="element-204">
              <div className="flex justify-between items-center mb-1" data-id="element-205">
                <span className="text-xs text-gray-500" data-id="element-206">On-Time Delivery</span>
                <span className="text-xs font-medium" data-id="element-207">
                  {supplier.metrics?.onTimeDelivery || '92'}%
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-208">
                <div className="h-full bg-[#4caf50] rounded-full" style={{
                width: `${supplier.metrics?.onTimeDelivery || 92}%`
              }} data-id="element-209"></div>
              </div>
            </div>
            <div data-id="element-210">
              <div className="flex justify-between items-center mb-1" data-id="element-211">
                <span className="text-xs text-gray-500" data-id="element-212">Quality Rating</span>
                <span className="text-xs font-medium" data-id="element-213">
                  {supplier.metrics?.qualityRating || '88'}%
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-214">
                <div className="h-full bg-[#005a9e] rounded-full" style={{
                width: `${supplier.metrics?.qualityRating || 88}%`
              }} data-id="element-215"></div>
              </div>
            </div>
            <div data-id="element-216">
              <div className="flex justify-between items-center mb-1" data-id="element-217">
                <span className="text-xs text-gray-500" data-id="element-218">Risk Score</span>
                <span className="text-xs font-medium" data-id="element-219">
                  {supplier.metrics?.riskScore || '18'}%
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-220">
                <div className="h-full bg-[#e53935] rounded-full" style={{
                width: `${supplier.metrics?.riskScore || 18}%`
              }} data-id="element-221"></div>
              </div>
            </div>
          </div>
        </div>
        {/* Performance Trend Chart */}
        <div className="mb-6" data-id="element-222">
          <div className="flex items-center mb-3" data-id="element-223">
            <BarChart3Icon size={16} className="text-gray-500 mr-2" data-id="element-224" />
            <h4 className="text-sm font-medium text-gray-500" data-id="element-225">
              Performance Trend
            </h4>
          </div>
          <div className="bg-white p-3 border border-gray-200 rounded-lg" data-id="element-226">
            <ResponsiveContainer width="100%" height={200} data-id="element-227">
              <LineChart data={performanceData} margin={{
              top: 5,
              right: 5,
              left: 0,
              bottom: 5
            }} data-id="element-228">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-229" />
                <XAxis dataKey="month" tick={{
                fontSize: 10
              }} data-id="element-230" />
                <YAxis domain={[60, 100]} tick={{
                fontSize: 10
              }} data-id="element-231" />
                <Tooltip data-id="element-232" />
                <Legend wrapperStyle={{
                fontSize: '10px'
              }} data-id="element-233" />
                <Line type="monotone" dataKey="onTimeDelivery" name="On-Time Delivery" stroke="#4caf50" strokeWidth={2} dot={{
                r: 3
              }} activeDot={{
                r: 5
              }} data-id="element-234" />
                <Line type="monotone" dataKey="qualityRating" name="Quality Rating" stroke="#005a9e" strokeWidth={2} dot={{
                r: 3
              }} activeDot={{
                r: 5
              }} data-id="element-235" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Volume by Product Category */}
        <div className="mb-6" data-id="element-236">
          <div className="flex items-center mb-3" data-id="element-237">
            <PackageIcon size={16} className="text-gray-500 mr-2" data-id="element-238" />
            <h4 className="text-sm font-medium text-gray-500" data-id="element-239">
              Volume by Product
            </h4>
          </div>
          <div className="bg-white p-3 border border-gray-200 rounded-lg" data-id="element-240">
            <ResponsiveContainer width="100%" height={180} data-id="element-241">
              <BarChart data={volumeData} margin={{
              top: 5,
              right: 5,
              left: 0,
              bottom: 5
            }} data-id="element-242">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-243" />
                <XAxis dataKey="category" tick={{
                fontSize: 10
              }} data-id="element-244" />
                <YAxis tick={{
                fontSize: 10
              }} data-id="element-245" />
                <Tooltip data-id="element-246" />
                <Bar dataKey="volume" name="Volume (units)" fill="#005a9e" data-id="element-247" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Recent shipments */}
        <div className="mb-6" data-id="element-248">
          <div className="flex items-center mb-3" data-id="element-249">
            <TruckIcon size={16} className="text-gray-500 mr-2" data-id="element-250" />
            <h4 className="text-sm font-medium text-gray-500" data-id="element-251">
              Recent Shipments
            </h4>
          </div>
          <div className="space-y-3" data-id="element-252">
            {[1, 2, 3].map(i => <div key={i} className="bg-white p-3 border border-gray-200 rounded-md" data-id="element-253">
                <div className="flex justify-between items-center" data-id="element-254">
                  <span className="text-sm font-medium" data-id="element-255">
                    Shipment #{1000 + i}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${i === 1 ? 'bg-green-50 text-green-700' : i === 2 ? 'bg-yellow-50 text-yellow-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-256">
                    {i === 1 ? 'Delivered' : i === 2 ? 'In Transit' : 'Scheduled'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-257">
                  {i === 1 ? 'Delivered on Oct 15, 2023' : i === 2 ? 'Expected arrival: Oct 25, 2023' : 'Departure: Nov 5, 2023'}
                </p>
              </div>)}
          </div>
        </div>
        {/* Related suppliers */}
        <div data-id="element-258">
          <h4 className="text-sm font-medium text-gray-500 mb-3" data-id="element-259">
            Connected Suppliers
          </h4>
          <div className="space-y-2" data-id="element-260">
            {supplier.connections?.slice(0, 3).map(connId => <div key={connId} className="flex justify-between items-center p-2 bg-white border border-gray-200 rounded-md" data-id="element-261">
                <div className="flex items-center" data-id="element-262">
                  <div className="w-8 h-8 rounded-full bg-[#005a9e] text-white flex items-center justify-center text-xs font-medium" data-id="element-263">
                    {connId.charAt(0).toUpperCase()}
                  </div>
                  <span className="ml-2 text-sm" data-id="element-264">Supplier {connId}</span>
                </div>
                <span className="text-xs text-gray-500" data-id="element-265">
                  Tier {Math.min(supplier.tier + 1, 3)}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
};
export default SupplierDetailPanel;